import type {InvestmentPeriodsQuery} from '@transactions/graphql';

export type Period = InvestmentPeriodsQuery['investmentPeriods'][number];

/** A period's numeric parts, the ones that sum. */
export type PeriodPart =
  | 'balanceChange'
  | 'saved'
  | 'income'
  | 'fees'
  | 'marketChange'
  | 'realized'
  | 'unrealizedChange'
  | 'unclassified'
  | 'residualChange';

/** A part summed over periods: what's known, how many were, and whether that's all. */
export type PartSum = {cents: number; known: number; complete: boolean};

/**
 * The periods to total, each counted once: a company's period stands for
 * its linked account's, so that account's own is left out.
 */
export const countedPeriods = (periods: Period[]): Period[] => {
  const linked = new Set(periods.flatMap((p) => (p.issuerId != null && p.accountId != null ? [p.accountId] : [])));
  return periods.filter((p) => p.issuerId != null || p.accountId == null || !linked.has(p.accountId));
};

export const partOf = (period: Period, key: PeriodPart): number | null => period[key] ?? null;

export const sumPart = (periods: Period[], key: PeriodPart): PartSum => {
  const values = periods.map((period) => partOf(period, key));
  return {
    cents: values.reduce<number>((total, value) => total + (value ?? 0), 0),
    known: values.filter((value) => value != null).length,
    complete: values.every((value) => value != null),
  };
};
